import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import Layout from './Layout';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/api';

const RegistrarNovedad = () => {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedEvent, setSelectedEvent] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [horaNovedad, setHoraNovedad] = useState('');
  const [fotoArchivo, setFotoArchivo] = useState(null);
  const [fotoPreview, setFotoPreview] = useState('');
  const [lastBitacoraId, setLastBitacoraId] = useState(null);
  const capturaInputId = useMemo(() => `novedad-foto-captura-${uuidv4()}`, []);

  const hasPuestoAsignado = useMemo(() => Boolean(user?.id_generado), [user]);

  const resetForm = useCallback(() => {
    setSelectedEvent('');
    setObservaciones('');
    setHoraNovedad('');
    setFotoArchivo(null);
    setFotoPreview('');
  }, []);

  const fetchStatus = useCallback(
    async (showLoader = true) => {
      if (!user) return;

      if (!hasPuestoAsignado) {
        setStatus(null);
        setError('Tu usuario no tiene un puesto asignado. Solicita a un administrador que configure tu acceso.');
        setLoading(false);
        return;
      }

      if (showLoader) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }
      setError('');
      setSuccess('');

      try {
        const url = new URL(getApiUrl('BITACORA', 'STATUS'));
        url.searchParams.set('idGenerado', user.id_generado);
        url.searchParams.set('username', user.username || '');
        url.searchParams.set('appid', user.appid || '');

        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });

        const data = await response.json();

        if (response.ok && data.success) {
          setStatus(data.data);
          setLastBitacoraId(null);
          resetForm();
        } else {
          setStatus(null);
          setError(data.error || 'No fue posible obtener el estado de la bitácora.');
        }
      } catch (err) {
        console.error('Error cargando estado de bitácora:', err);
        setStatus(null);
        setError('Error de conexión al cargar el estado de la bitácora.');
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [user, hasPuestoAsignado, resetForm]
  );

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    if (!fotoPreview) return undefined;
    return () => {
      URL.revokeObjectURL(fotoPreview);
    };
  }, [fotoPreview]);

  const handleEventSelection = (event) => {
    setSelectedEvent(event.target.value);
    setObservaciones('');
    setHoraNovedad('');
    setFotoArchivo(null);
    setFotoPreview('');
    setSuccess('');
    setError('');
  };

  const optimizeImage = useCallback((file, { maxWidth = 1024, quality = 0.7 } = {}) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      const img = new Image();

      reader.onload = (e) => {
        img.src = e.target.result;
      };

      reader.onerror = reject;

      img.onload = () => {
        const scale = Math.min(maxWidth / img.width, 1);
        const canvas = document.createElement('canvas');
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('No se pudo optimizar la imagen.'));
              return;
            }

            const baseName = file.name.replace(/\.[^/.]+$/, '');
            const optimizedFile = new File([blob], `${baseName}.jpg`, {
              type: 'image/jpeg',
              lastModified: Date.now()
            });

            ctx.clearRect(0, 0, canvas.width, canvas.height);
            canvas.width = 0;
            canvas.height = 0;

            resolve(optimizedFile);
          },
          'image/jpeg',
          quality
        );
      };

      img.onerror = reject;
      reader.readAsDataURL(file);
    });
  }, []);

  const handleFileChange = useCallback(
    async (event) => {
      const file = event.target.files?.[0] || null;

      if (!file) {
        setFotoArchivo(null);
        setFotoPreview('');
        return;
      }

      if (!file.type.startsWith('image/')) {
        setError('Solo se permiten imágenes para la foto de la novedad.');
        event.target.value = '';
        return;
      }

      let finalFile = file;
      const ONE_MB = 1024 * 1024;

      try {
        if (file.size > ONE_MB) {
          const optimized = await optimizeImage(file, { maxWidth: 1024, quality: 0.7 });
          console.log(`Original: ${(file.size / 1024).toFixed(1)} KB`);
          console.log(`Optimizada: ${(optimized.size / 1024).toFixed(1)} KB`);
          finalFile = optimized;
        } else {
          console.log(`Imagen menor a 1MB, se enviará sin cambios (${(file.size / 1024).toFixed(1)} KB).`);
        }
      } catch (optimizationError) {
        console.error('Error optimizando imagen:', optimizationError);
        setError('No se pudo optimizar la imagen. Intenta con otra foto.');
        event.target.value = '';
        return;
      }

      setFotoArchivo(finalFile);
      setFotoPreview(URL.createObjectURL(finalFile));
    },
    [optimizeImage]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!status || !selectedEvent) {
      setError('Debes seleccionar un tipo de evento.');
      return;
    }

    if (status.puedeBitacora !== 'SI') {
      setError('La bitácora está deshabilitada. Primero debes relevar el puesto.');
      return;
    }

    if (selectedEvent === 'NOVEDAD' && !horaNovedad) {
      setError('Debes indicar la hora de la novedad.');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      // Si es NOVEDAD y hay foto, usar FormData, sino usar JSON
      const hasFoto = selectedEvent === 'NOVEDAD' && fotoArchivo;

      let response;
      if (hasFoto) {
        const formData = new FormData();
        formData.append('idGenerado', status.idGenerado);
        formData.append('tipo', selectedEvent);
        formData.append('observaciones', observaciones);
        formData.append('horaNovedad', horaNovedad);
        formData.append('username', user?.username || '');
        formData.append('appid', user?.appid || '');
        formData.append('foto', fotoArchivo);

        response = await fetch(getApiUrl('BITACORA', 'REGISTRAR_EVENTO'), {
          method: 'POST',
          body: formData
        });
      } else {
      const payload = {
        idGenerado: status.idGenerado,
        tipo: selectedEvent,
        observaciones,
        horaNovedad,
        username: user?.username || '',
        appid: user?.appid || ''
      };

        response = await fetch(getApiUrl('BITACORA', 'REGISTRAR_EVENTO'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      }

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(data.message || 'Evento registrado correctamente.');
        setLastBitacoraId(data.bitacoraId || null);
        resetForm();
        await fetchStatus(false);
      } else {
        setError(data.error || 'No fue posible registrar el evento.');
      }
    } catch (err) {
      console.error('Error guardando evento de bitácora:', err);
      setError('Error de conexión al registrar el evento.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderEventOptions = () => {
    if (!status?.eventOptions || status.eventOptions.length === 0) {
      return null;
    }

    return (
      <form className="bg-white border border-gray-200 rounded-xl shadow p-6 space-y-4">
        <div>
          <label htmlFor="tipo-evento" className="block text-sm font-medium text-gray-700 mb-1">
            Tipo de evento
          </label>
          <select
            id="tipo-evento"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            value={selectedEvent}
            onChange={handleEventSelection}
            disabled={status.puedeBitacora !== 'SI' || submitting}
          >
            <option value="">Selecciona evento</option>
            {status.eventOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        {status.puedeBitacora !== 'SI' && (
          <p className="text-xs text-red-600 font-medium">
            Bitácora deshabilitada, primero debe relevar el puesto.
          </p>
        )}
      </form>
    );
  };

  const renderSelectedEventForm = () => {
    if (!selectedEvent) return null;

    return (
      <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-xl shadow p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold text-gray-900">
            {selectedEvent === 'RONDA' ? 'Registrar ronda' : 'Registrar novedad'}
          </h3>
          <button
            type="button"
            className="px-3 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50"
            onClick={resetForm}
            disabled={submitting}
          >
            Cambiar evento
          </button>
        </div>

        {selectedEvent === 'NOVEDAD' && (
          <div>
            <label htmlFor="hora-novedad" className="block text-sm font-medium text-gray-700 mb-1">
              Hora de novedad
            </label>
            <input
              id="hora-novedad"
              type="time"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              value={horaNovedad}
              onChange={(e) => setHoraNovedad(e.target.value)}
              required
              disabled={submitting}
            />
          </div>
        )}

        <div>
          <label htmlFor="observaciones" className="block text-sm font-medium text-gray-700 mb-1">
            Observaciones
          </label>
          <textarea
            id="observaciones"
            rows={4}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="Agrega observaciones relevantes"
            value={observaciones}
            onChange={(e) => setObservaciones(e.target.value)}
            disabled={submitting}
          />
        </div>

        {selectedEvent === 'NOVEDAD' && (
          <div>
            <label htmlFor="novedad-foto" className="block text-sm font-medium text-gray-700 mb-1">
              Foto (opcional)
            </label>
            <div className="space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => document.getElementById(capturaInputId)?.click()}
                  className="inline-flex justify-center items-center w-full sm:w-[140px] px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg shadow hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-60"
                  disabled={submitting || status?.puedeBitacora !== 'SI'}
                >
                  Capturar foto
                </button>
              </div>

              <input
                id={capturaInputId}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={handleFileChange}
                disabled={submitting || status?.puedeBitacora !== 'SI'}
              />
            </div>
            {fotoPreview && (
              <div className="mt-3">
                <p className="text-xs text-gray-500 mb-1">Previsualización:</p>
                <img
                  src={fotoPreview}
                  alt="Foto de la novedad"
                  className="w-full max-w-xs rounded-lg border border-gray-200"
                />
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-end">
          <button
            type="submit"
            className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg shadow hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-60"
            disabled={submitting}
          >
            {submitting ? (
              <span className="flex items-center space-x-2">
                <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span>Guardando...</span>
              </span>
            ) : (
              'Guardar'
            )}
          </button>
        </div>
      </form>
    );
  };

  const renderEventosRecientes = () => {
    if (!status?.eventosRecientes || status.eventosRecientes.length === 0) {
      return (
        <div className="bg-white border border-gray-200 rounded-xl shadow p-6">
          <p className="text-sm text-gray-600">No hay eventos registrados recientemente.</p>
        </div>
      );
    }

    return (
      <div className="bg-white border border-gray-200 rounded-xl shadow overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Últimos eventos</h2>
          <p className="text-sm text-gray-500">
            Mostrando los 10 registros más recientes del puesto.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Evento</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Hora</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Observación</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {status.eventosRecientes.map((evento, index) => (
                <tr key={`${evento.tipo_evento}-${evento.hora_registro_servidor}-${index}`}>
                  <td className="px-4 py-3 text-sm font-medium text-indigo-600">{evento.tipo_evento}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {evento.hora_ingreso || new Date(evento.hora_registro_servidor).toLocaleTimeString('es-EC', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">{evento.observacion || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <div className="p-3 sm:p-4 md:p-6 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Registrar evento</h1>
            <p className="text-gray-600">
              Gestiona los eventos del puesto asignado dentro de la bitácora.
            </p>
          </div>
          <button
            type="button"
            onClick={() => fetchStatus(false)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-60"
            disabled={loading || refreshing || submitting}
          >
            {refreshing ? (
              <span className="flex items-center space-x-2">
                <svg className="animate-spin h-4 w-4 text-gray-500" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span>Actualizando...</span>
              </span>
            ) : (
              'Actualizar estado'
            )}
          </button>
        </div>

        {loading ? (
          <div className="bg-white border border-gray-200 rounded-xl p-6 flex items-center justify-center">
            <div className="flex items-center space-x-3">
              <svg className="animate-spin h-5 w-5 text-indigo-600" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span className="text-sm font-medium text-gray-700">Cargando estado de la bitácora...</span>
            </div>
          </div>
        ) : (
          <>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4">
                <p className="text-sm font-medium">{error}</p>
              </div>
            )}

            {success && (
              <div className="bg-green-50 border border-green-200 text-green-800 rounded-xl p-4 space-y-1">
                <p className="text-sm font-medium">{success}</p>
                {lastBitacoraId && (
                  <p className="text-xs text-green-700">
                    ID de bitácora generado: <span className="font-semibold">{lastBitacoraId}</span>
                  </p>
                )}
              </div>
            )}

            {hasPuestoAsignado && renderEventOptions()}
            {hasPuestoAsignado && renderSelectedEventForm()}
            {renderEventosRecientes()}
          </>
        )}
      </div>
    </Layout>
  );
};

export default RegistrarNovedad;

